/**
 * @file common.constants.ts
 * @module utils/common.constants
 * @description Common constants shared across the application.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { CSSProperties } from "react";
import { OrderCard, OrderCash, OrderMPesa, OrderTigoPesa } from "./constants";

/** `OrderStatus` consisting values which represents order status  */
export enum OrderStatus {
  NEW_ORDER = 1, //New-order-placed
  Processed = 2, //Route-planning generated
  // OutForDelivery = 3//out for delivery [Removed]
  Delivered = 4, //Delivered
  Cancelled = 5, //Cancelled
}

/** `OrderStatusName` consisting values which represents order status name  */
export const OrderStatusName: Record<
  OrderStatus,
  { statusName: string; label?: string }
> = {
  [OrderStatus.NEW_ORDER]: { statusName: "New Order", label: "Order Placed" },
  [OrderStatus.Processed]: { statusName: "Order Processed" },
  [OrderStatus.Delivered]: { statusName: "Order Delivered" },
  [OrderStatus.Cancelled]: { statusName: "Order Cancelled" },
};

/** `STATUS_UI_MAP` consisting values which contains color code and label  */
export const STATUS_UI_MAP: Record<
  number,
  { color: string; badgeClass: string; label: string }
> = {
  [OrderStatus.NEW_ORDER]: {
    color: "#0D6EFD",
    badgeClass: "bg-light-primary text-primary",
    label: OrderStatusName[OrderStatus.NEW_ORDER].label ?? "",
  },
  [OrderStatus.Processed]: {
    color: "#FFC107",
    badgeClass: "bg-light-warning text-warning",
    label: OrderStatusName[OrderStatus.Processed].statusName,
  },
  [OrderStatus.Delivered]: {
    color: "#198754",
    badgeClass: "bg-light-success text-success",
    label: OrderStatusName[OrderStatus.Delivered].statusName,
  },
  [OrderStatus.Cancelled]: {
    color: "#DC3545",
    badgeClass: "bg-light-danger text-danger",
    label: OrderStatusName[OrderStatus.Cancelled].statusName,
  },
};

/** `QuantityTypes` enum value related to `Quantity Types` */
export enum QuantityTypes {
  CartonWithDozens = 1,
  Dozen = 2,
  Piece = 3,
  CartonWithPieces = 4,
}

/** `QuantityTypesLabel` object provides `label` value for QuantityType */
export const QuantityTypesLabel = {
  1: "Carton With Dozens",
  2: "Dozen With Pieces",
  3: "Piece",
  4: "Carton With Pieces",
};

/** `DbQuantityKeys` represents the keys which are stored in `Product` `Document`  */
export const DbQuantityKeys = {
  1: "dozensInCarton",
  2: "piecesInDozen",
  4: "piecesInCarton",
};

/** QuantityTypes in small case */
export type QuantityTypeSmallCase = "piece" | "dozen" | "cwd" | "cwp";

export enum QtyTypeMappingInSmallCase {
  cwd = 1,
  dozen = 2,
  piece = 3,
  cwp = 4,
}
// Order UI Identifiers
export const MoveToPendingMsg = "Order moved to pending and can be reprocessed";
export const OrderReprocessableMsg =
  "This order has remaining quantities and can be reprocessed";
export const ScheduleForNextDayMsg = "Scheduled for next day delivery";
export const NewCustomerPriorityOrderMsg =
  "It's a new customer priority order and can be processed immediately";
export const PartialDeliverMsg =
  "This order has been partially delivered. Remaining quantities can be reprocessed";

export const RouteVehicleVariantStatus = {
  1: " Not Loaded",
  2: " Loaded",
  3: " Line Item Removed",
};


const VALID_QUANTITY_TYPES: Set<number> = new Set([
  QuantityTypes.CartonWithDozens,
  QuantityTypes.Dozen,
  QuantityTypes.Piece,
  QuantityTypes.CartonWithPieces,
]);

export const isValidQuantityType = (type: any) =>
  VALID_QUANTITY_TYPES.has(Number(type));

// Transaction payment codes differ from the order's paymentMode codes, so each has its own map.
export const TRANSACTION_PAYMENT_LABELS: Record<number, string> = {
  2: "Cash",
  5: "Tigo pesa",
  3: "MPesa",
  4: "Card",
};

export const ORDER_PAYMENT_MODE_LABELS: Record<number, string> = {
  [OrderCash]: "Cash",
  [OrderTigoPesa]: "Tigo pesa",
  [OrderMPesa]: "MPesa",
  [OrderCard]: "Card",
};

// Bootstrap's default 0.9 tooltip opacity lets the white table show through the text.
export const TOOLTIP_OPAQUE_STYLE = {
  ['--bs-tooltip-opacity' as string]: '1',
} as CSSProperties;
